import get from 'lodash/get';

import * as actions from 'store/profile/actions';
import { AnyAction } from 'redux';
import { Nullable, RequestState } from 'modules/types/common';
import { Insurance } from 'store/findProvider/types';
import { normalizeProfile } from './mappers';
import { Consumer, Grantor, Grantee, RequestedProxy, ProfileState } from './types';

const startState = (state: {}) => ({
  ...state,
  isFetching: true,
  error: null,
  profileRequestState: RequestState.REQUESTING
});

const startStateForInsurance = (state: {}) => ({
  ...state,
  insuranceRequestState: RequestState.REQUESTING,
  error: null
});

const failState = (state: {}, action: {}) => ({
  ...state,
  isFetching: false,
  error: get(action, 'error'),
  profileRequestState: RequestState.ERROR
});

const failStateForInsurance = (state: {}, action: {}) => ({
  ...state,
  insuranceRequestState: RequestState.ERROR,
  error: get(action, 'error')
});

export const initialValidationState = {
  status: null,
  type: null,
  error: null
};

export const profileInitialState: ProfileState = {
  isHydrated: false,
  isFetching: false,
  error: null,
  visitpayEnrolledAndAccessible: false,
  visitpayEnrolledRequestState: RequestState.INITIALIZED,
  insuranceRequestState: RequestState.INITIALIZED,
  lastUpdated: null,

  // Top level Profile keys
  proxyToUpdate: {} as RequestedProxy,
  customAttributes: [],
  requestedProxies: [],
  grantors: [],
  grantees: [],
  insurances: [],
  activeInsurance: null,
  preferredProviders: [],
  preferredLocations: [],
  validation: initialValidationState,
  consumer: {} as Consumer,
  profileRequestState: RequestState.INITIALIZED
};

const grabError = (action, type) => ({
  type
});

const profileReducer = (state = profileInitialState, action: AnyAction) => {
  switch (action.type) {
    case actions.GET_PROFILE_FOR_CONSUMER:
    case actions.GET_PROFILE: {
      return startState(state);
    }
    case actions.GET_PROFILE_SUCCESS:
    case actions.UPDATE_CONSUMER_PROFILE_SUCCESS: {
      return {
        ...state,
        ...normalizeProfile(get(action, 'payload.data')),
        lastUpdated: Date.now(),
        isFetching: false,
        isHydrated: true,
        profileRequestState: RequestState.SUCCESS
      };
    }
    case actions.GET_PROFILE_FOR_CONSUMER_SUCCESS: {
      return {
        ...state,
        isFetching: false,
        error: null,
        profileRequestState: RequestState.SUCCESS
      };
    }
    case actions.GET_PROFILE_FAIL:
      return failState(state, action);
    case actions.GET_PROFILE_FOR_INSURANCE:
    case actions.GET_PROFILE_FOR_CONSUMER_INSURANCE:
      return startStateForInsurance(state);
    case actions.GET_PROFILE_FOR_CONSUMER_INSURANCE_SUCCESS:
    case actions.GET_PROFILE_FOR_INSURANCE_SUCCESS: {
      return {
        ...state,
        insurances: get(action, 'payload.data'),
        lastUpdated: Date.now(),
        insuranceRequestState: RequestState.SUCCESS
      };
    }
    case actions.GET_PROFILE_FOR_CONSUMER_FAIL:
    case actions.GET_PROFILE_FOR_CONSUMER_INSURANCE_FAIL:
    case actions.GET_PROFILE_FOR_INSURANCE_FAIL: {
      return failStateForInsurance(state, action);
    }
    case actions.SET_ACTIVE_INSURANCE:
      return {
        ...state,
        activeInsurance: action.payload
      };
    case actions.ACCEPT_T_AND_C:
      return {
        ...state,
        termAcceptance: { version: action.payload.version, acceptanceDate: action.payload.time }
      };
    case actions.RESET_VALIDATION:
    case actions.SEND_PIN:
      return {
        ...state,
        validation: initialValidationState
      };
    case actions.SEND_PIN_FAIL:
      return {
        ...state,
        validation: {
          error: grabError(action, actions.SEND_PIN_FAIL)
        }
      };
    case actions.VALIDATE_PIN_SUCCESS:
      return {
        ...state,
        validation: {
          status: get(action, 'payload.data.statusCode', ''),
          error: null
        }
      };
    case actions.VALIDATE_PIN_FAIL:
      return {
        ...state,
        validation: {
          error: grabError(action, actions.VALIDATE_PIN_FAIL)
        }
      };
    case actions.ADD_REQUESTED_PROXIES:
      return {
        ...state,
        requestedProxies: [...state.requestedProxies, ...action.payload.data]
      };
    case actions.CLEAR_REQUESTED_PROXIES:
      return {
        ...state,
        requestedProxies: [],
        proxyToUpdate: {} as RequestedProxy
      };
    case actions.DELETE_REQUESTED_PROXY:
      return {
        ...state,
        requestedProxies: [...action.payload.data]
      };
    case actions.SET_PROXY_TO_UPDATE:
      return {
        ...state,
        proxyToUpdate: action.payload.data
      };
    case actions.RESET_PROXY_TO_UPDATE:
      return {
        ...state,
        proxyToUpdate: {}
      };
    case actions.UPDATE_PROXY:
      return {
        ...state,
        requestedProxies: action.payload.data,
        proxyToUpdate: {} as RequestedProxy
      };
    case actions.GET_VISITPAY_ENROLLED_INFO: {
      return {
        ...state,
        visitpayEnrolledRequestState: RequestState.REQUESTING
      };
    }
    case actions.GET_VISITPAY_ENROLLED_INFO_SUCCESS: {
      return {
        ...state,
        visitpayEnrolledRequestState: RequestState.SUCCESS,
        visitpayEnrolledAndAccessible: action.payload.data.isEnrolled
      };
    }
    case actions.GET_VISITPAY_ENROLLED_INFO_FAIL: {
      return {
        ...state,
        visitpayEnrolledRequestState: RequestState.ERROR,
        visitpayEnrolledAndAccessible: false
      };
    }

    default:
      return state;
  }
};

export default profileReducer;
